'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import ReportIcon from '@mui/icons-material/Report';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import debounce from 'lodash.debounce';
import { useCallback, useMemo, useState } from 'react';
import {
	DataGrid,
	DataGridSortModel,
	FilterAutoCompleteById,
	FilterSearch,
	FilterSelect,
	RequirementChip,
} from '@repo/components';
import GenericDataPageHeader from 'src/components/GenericDataPageHeader';
import InhoudingsplichtChip from 'src/components/InhoudingsplichtChip';
import { PERMISSIONS } from '@repo/shared/domain';
import { GroupForm } from '@repo/forms';
import { EventEmitter } from '@repo/shared/helpers';
import { reportDownload } from '@repo/shared/helpers';
import { getVerifyPermission } from '@repo/shared/helpers';
import { guardedClient as request } from '@repo/shared/helpers';
import { sortToString, stringToSort, constructQuery } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getGroups } from 'src/api/admin/groups';
import { getGroupTypes } from 'src/api/admin/types';
import { getRolesForAutocomplete } from 'src/api/admin/roles';
import { getReport } from 'src/helpers/getReport';
import { useConfig } from 'src/hooks/useConfig';

export const Route = createFileRoute('/_admin/groups/')({
	validateSearch: (search: Record<string, unknown>) => {
		return {
			tab: (search.tab as string) || undefined,
			sort: (search.sort as string) || undefined,
			page: (search.page as number) || undefined,
			search: (search.search as string) || undefined,
			typeIds: (search.typeIds as string) || undefined,
			groupStatus: (search.groupStatus as string) || undefined,
		};
	},
	loaderDeps: ({ search }) => search,
	loader: async ({ deps }) => {
		const query = constructQuery({
			tab: deps.tab,
			sort: deps.sort,
			page: deps.page,
			search: deps.search,
			typeIds: deps.typeIds,
			groupStatus: deps.groupStatus,
		});

		const inReviewQuery = constructQuery({ tab: '1' });

		const [types, roles, data, groupsInReview] = await Promise.all([
			getGroupTypes(),
			getRolesForAutocomplete(),
			getGroups(query),
			getGroups(inReviewQuery),
		]);

		return { types: types.data, roles, data, amountOfGroupsInReview: groupsInReview.count };
	},
	component: GroupsPage,
});

const downloadReport = async (query: string) => {
	const blob = await getReport('groups', query);
	if (!blob) {
		return;
	}
	reportDownload(blob);
};

function GroupsPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const searchParams = Route.useSearch();
	const {
		types: initialTypes,
		roles: initialRoles,
		data: initialData,
		amountOfGroupsInReview: initialAmountInReview,
	} = Route.useLoaderData();
	const { user: authenticatedUser } = useAuthenticatedUser();
	const { config } = useConfig();
	const featureFlags = config?.featureFlags || {};
	const usesCompanyApiChecks = featureFlags.USES_COMPANY_API_CHECKS || false;

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	useBreadcrumbLevel(0, 'Companies');

	const updateQueryParams = useUpdateQueryParams();
	const [isFormOpened, setIsFormOpened] = useState(false);

	const query = constructQuery({
		tab: searchParams.tab,
		sort: searchParams.sort,
		page: searchParams.page,
		search: searchParams.search,
		typeIds: searchParams.typeIds,
		groupStatus: searchParams.groupStatus,
	});

	const { data: groupsData, refetch: refetchGroups } = useQuery({
		queryKey: ['groups', query],
		queryFn: () => getGroups(query),
		initialData: initialData,
	});

	const { data: types } = useQuery({
		queryKey: ['group-types'],
		queryFn: () => getGroupTypes(),
		initialData: { data: initialTypes },
	});

	const { data: roles } = useQuery({
		queryKey: ['roles-autocomplete'],
		queryFn: () => getRolesForAutocomplete(),
		initialData: initialRoles,
	});

	const logTypes = [
		{ value: 'all', label: 'All' },
		{ value: 'pending', label: 'Pending' },
		{ value: 'inReview', label: 'In Review' },
		{ value: 'accepted', label: 'Accepted' },
		{ value: 'expired', label: 'Expired' },
		{ value: 'expiryWarning', label: 'Expiry Warning' },
	];

	const columns = [
		{
			field: 'markedForReview',
			headerName: '',
			renderCell: (c) => c.row.markedForReview?.length > 0 && <ReportIcon size="small" color="error" />,
			width: 5,
		},
		{
			field: 'name',
			headerName: 'Name',
			flex: 1,
		},
		{
			field: 'type',
			headerName: 'Type',
			flex: 1,
			renderCell: (c) => <RequirementChip object={c.row} />,
			sortable: false,
		},
		{
			field: 'uniqueId',
			headerName: 'VAT',
			flex: 1,
		},
		{
			field: 'description',
			headerName: 'Description',
			flex: 2,
		},
		...(usesCompanyApiChecks
			? [
					{
						field: 'companyData',
						headerName: 'Company checks',
						flex: 1,
						sortable: false,
						renderCell: (c) => (c.row.companyData ? <InhoudingsplichtChip companyData={c.row.companyData} /> : null),
					},
				]
			: []),
	];

	const downloadCSVReport = useCallback(async () => {
		downloadReport(query);
	}, [query]);

	const handleSort = ([sort]: DataGridSortModel) => {
		updateQueryParams({ sort: sort ? sortToString(sort) : undefined });
	};

	const handleRowClick = ({ row }) => {
		navigate({ to: '/groups/$groupId', params: { groupId: row.id } });
	};

	const handleSearchChange = (searchValue: string) => {
		updateQueryParams({ search: searchValue || undefined });
	};

	const handleTabChange = (tab: string) => {
		updateQueryParams({ tab });
	};

	const handleFilterChange = (filterData: Record<string, string | string[] | undefined>) => {
		updateQueryParams(filterData);
	};

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page: page });
	};

	const handleCloseForm = () => {
		setIsFormOpened(false);
	};

	const handleAddGroup = async (formData) => {
		let isError;
		if (formData) {
			request
				.post('api/groups', formData)
				.then(({ body }: { body: any }) => {
					if (body.userCreationError) {
						const failedEmails = body.userCreationError.emails?.join(', ');
						const message = failedEmails
							? `Company created successfully, but failed to create admin accounts for: ${failedEmails}. You can add admins manually later.`
							: 'Company created successfully, but some admin accounts could not be created. You can add admins manually later.';

						EventEmitter.emit('toast', {
							type: 'warning',
							message,
						});
					} else {
						EventEmitter.emit('toast', {
							type: 'success',
							message: 'Company saved',
						});
					}
					navigate({ to: '/groups/$groupId', params: { groupId: body.id } });
				})
				.catch(() => {
					isError = true;
					EventEmitter.emit('toast', {
						type: 'error',
						message: 'Failed to save Company. Did you enter all data correctly?',
					});
				});
		}

		refetchGroups();
		if (!isError) {
			setIsFormOpened(false);
		}
	};

	const handleOpenForm = useCallback(() => {
		setIsFormOpened(true);
	}, []);

	const handleReload = () => {
		refetchGroups();
	};

	const actions = useMemo(() => {
		return verifyPermission(PERMISSIONS.adminGroupsCreate)
			? [
					<Button onClick={handleOpenForm} startIcon={<AddIcon />} variant="outlined" key="add">
						Add Company
					</Button>,
				]
			: [];
	}, [handleOpenForm, verifyPermission]);

	const defaultGroupData = {};

	const tabs = [
		{
			id: '0',
			label: 'Active',
		},
		{
			id: '1',
			label: 'In Review',
			error: initialAmountInReview ? `${initialAmountInReview}` : undefined,
		},
		{
			id: '2',
			label: 'Archived',
		},
		{
			id: '3',
			label: 'Blocked',
		},
	];

	return (
		<>
			<GenericDataPageHeader
				title="Company"
				tabs={tabs}
				defaultTab="0"
				onTabChange={handleTabChange}
				actions={actions}
				downloadCSVReport={downloadCSVReport}
			/>
			<div className="p-4 pt-0">
				<div className="mt-2 flex flex-row flex-wrap gap-2">
					<FilterSearch
						label="Search Company"
						defaultValue={searchParams.search || ''}
						onChange={debounce((searchInput: string) => {
							handleSearchChange(searchInput);
						}, 350)}
						sx={{ width: '30%', minWidth: '30%', flexGrow: 1 }}
					/>
					<FilterAutoCompleteById
						key="typeFilter"
						label="Filter Types"
						options={types?.data || initialTypes}
						value={searchParams.typeIds || []}
						onChange={(typeIds) => handleFilterChange({ typeIds })}
						sx={{ width: '30%' }}
						multiple
					/>
					<FilterSelect
						key="groupStatus"
						listSubHeader="Filter Company Statuses"
						options={logTypes}
						value={searchParams.groupStatus || ['all']}
						onChange={(groupStatus) => handleFilterChange({ groupStatus })}
						sx={{ width: '30%' }}
					/>
				</div>

				<Paper variant="outlined" sx={{ marginTop: 2 }}>
					<DataGrid
						data={groupsData?.data || []}
						columns={columns}
						rowCount={groupsData?.count || 0}
						paginationMode="server"
						sortingMode="server"
						onPaginationModelChange={handlePaginationModelChange}
						onSortModelChange={handleSort}
						sortingOrder={['asc', 'desc']}
						sortModel={[stringToSort(searchParams.sort || 'startTime:desc')]}
						pageSize={groupsData?.pageSize || 25}
						pageSizeOptions={[groupsData?.pageSize || 25]}
						page={groupsData?.page || 0}
						onRowClick={handleRowClick}
						pagination
						getRowClassName={(p) => (p.row.markedForReview?.length > 0 ? 'error' : '')}
						onReload={handleReload}
					/>
				</Paper>
			</div>

			{isFormOpened && (
				<GroupForm
					isOpen={true}
					data={defaultGroupData}
					onClose={handleCloseForm}
					onSubmit={handleAddGroup}
					types={types?.data || initialTypes}
					roles={roles || initialRoles}
				/>
			)}
		</>
	);
}
